import { useEffect } from 'react';
import { StickyBackBar } from '@/components/layout/StickyBackBar';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { useBatchUserSettings } from '@/api/hooks/useBatchUserSettings';
import { useSupportedCurrencies, useUpdateUserSettings } from '@/api/hooks/useUserSettings';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { UserSettingsForm } from '@/components/settings/UserSettingsForm';
import { getErrorMessage } from '@/utils/errorHandler';
import { usePageTitle } from '@/hooks/usePageTitle';
import { toast } from 'sonner';
import type { UpdateUserSettingsData } from '@/api/types';

export function PreferencesPage() {
    usePageTitle('Preferences');
    const { updateFromBatch } = useAuth();
    const { data: batchData, isLoading, error } = useBatchUserSettings();
    const { data: currenciesData } = useSupportedCurrencies();
    const updateSettings = useUpdateUserSettings();

    // Update AuthContext with batch data when it loads
    useEffect(() => {
        if (batchData?.profile?.user && batchData?.profile?.settings) {
            updateFromBatch(batchData.profile.user, batchData.profile.settings);
        }
    }, [batchData, updateFromBatch]);

    const handleSubmit = async (data: UpdateUserSettingsData) => {
        await updateSettings.mutateAsync(data);
        toast.success('Settings saved successfully');
    };

    const settings = batchData?.profile?.settings;
    const currencies = currenciesData?.currencies || {};
    const options = batchData?.options;

    return (
        <>
            <StickyBackBar destinationLabel="Settings" backPath="/settings" />
            <div className="space-y-6 pt-4">
                <PageHeader title="Preferences" />

                {isLoading && (
                <div className="flex justify-center py-12">
                    <div className="text-gray-500 dark:text-gray-400">
                        Loading preferences...
                    </div>
                </div>
            )}

            {error && (
                <ErrorAlert message={getErrorMessage(error)} />
            )}

            {settings && Object.keys(currencies).length > 0 && options && (
                <UserSettingsForm
                    settings={settings}
                    availableCurrencies={currencies}
                    options={options}
                    onSubmit={handleSubmit}
                    isLoading={updateSettings.isPending}
                />
            )}
            </div>
        </>
    );
}
